import {
  useGetAdminBackfillQueueQuery,
  useGetAdminCoverageImpactQuery,
  useGetAdminSchedulersQuery,
  useGetAdminSnapshotQueueQuery,
  useGetAdminTodoQuery,
} from "../../../../services";
import { SectionState } from "../../types";
import { toSectionState } from "../../utils/sectionState";
import { AdminOverviewSections, SectionRetries } from "../types";
import { deriveAttentionItems } from "../utils/deriveAttentionItems";

const dataOf = <T>(section: SectionState<T>): T | undefined =>
  section.state === "available" ? section.data : undefined;

export const useAdminOverview = () => {
  const snapshotQueue = useGetAdminSnapshotQueueQuery();
  const backfillQueue = useGetAdminBackfillQueueQuery();
  const coverageImpact = useGetAdminCoverageImpactQuery();
  const schedulers = useGetAdminSchedulersQuery();
  const todo = useGetAdminTodoQuery();

  const sections: AdminOverviewSections = {
    snapshotQueue: toSectionState(snapshotQueue),
    backfillQueue: toSectionState(backfillQueue),
    coverageImpact: toSectionState(coverageImpact),
    schedulers: toSectionState(schedulers),
    todo: toSectionState(todo),
  };

  const retries: SectionRetries = {
    snapshotQueue: () => void snapshotQueue.refetch(),
    backfillQueue: () => void backfillQueue.refetch(),
    coverageImpact: () => void coverageImpact.refetch(),
    schedulers: () => void schedulers.refetch(),
    todo: () => void todo.refetch(),
  };

  const attentionItems = deriveAttentionItems({
    snapshotQueue: dataOf(sections.snapshotQueue),
    backfillQueue: dataOf(sections.backfillQueue),
    coverageImpact: dataOf(sections.coverageImpact),
    schedulers: dataOf(sections.schedulers),
    todo: dataOf(sections.todo),
  });

  const isRefreshing = [
    snapshotQueue,
    backfillQueue,
    coverageImpact,
    schedulers,
    todo,
  ].some(({ isFetching }) => isFetching);

  const refreshAll = () => {
    Object.values(retries).forEach((retry) => retry());
  };

  return { sections, retries, attentionItems, isRefreshing, refreshAll };
};
